import React, { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { formatCompact } from "../../utils/formatCompact";
import { p2pParticipantRoleMeta } from "../../users/userP2PHelpers";

const COLLAPSED_COUNT = 4;

function ParticipantRow({ participant, perSeat }) {
  const role = p2pParticipantRoleMeta(participant.role);
  const pledged = Number(participant.pledge) || 0;
  const expected = Number(participant.expectedPledge) || 0;
  const showExpected = pledged <= 0 && expected > 0;
  const displayAmount = showExpected ? expected : pledged;

  return (
    <div className="ap2p-participant-row">
      <span className="ap2p-participant-avatar">
        {(participant.name || "?").trim().slice(0, 2).toUpperCase()}
      </span>
      <span className="ap2p-participant-body">
        <span className="ap2p-participant-top">
          <strong>{participant.name || "Member"}</strong>
          <span className={`ap2p-participant-role ap2p-participant-role-${role.tone}`}>
            {role.label}
          </span>
        </span>
      </span>
      <span className="ap2p-participant-pledge">
        <em>{formatCompact(displayAmount)}</em>
        <small>{showExpected ? "awaiting" : "pledged"}</small>
      </span>
    </div>
  );
}

export default function P2PParticipantsPanel({ duel }) {
  const participants = duel?.participants || [];
  const perSeat = Number(duel?.perSeatStake ?? duel?.stake) || 0;
  const [expanded, setExpanded] = useState(false);
  const isLong = participants.length > COLLAPSED_COUNT;
  const visible = expanded || !isLong ? participants : participants.slice(0, COLLAPSED_COUNT);
  const hiddenCount = Math.max(0, participants.length - COLLAPSED_COUNT);

  useEffect(() => {
    setExpanded(false);
  }, [duel?._id]);

  if (!participants.length) {
    return (
      <section className="ap2p-panel">
        <h3 className="ap2p-panel-title">Participants</h3>
        <p className="ap2p-panel-empty">No participants on this duel yet.</p>
      </section>
    );
  }

  return (
    <section className={`ap2p-panel ap2p-panel-participants${expanded ? " ap2p-panel-participants-open" : ""}`}>
      <button
        type="button"
        className="ap2p-panel-head ap2p-panel-head-toggle"
        onClick={() => isLong && setExpanded((value) => !value)}
        aria-expanded={isLong ? expanded : undefined}
        disabled={!isLong}
      >
        <h3 className="ap2p-panel-title">Participants</h3>
        <span className="ap2p-panel-head-trail">
          {perSeat > 0 ? (
            <span className="ap2p-panel-head-meta">{formatCompact(perSeat)} min stake</span>
          ) : null}
          {isLong ? (
            <ChevronDown
              size={14}
              className={`ap2p-panel-chevron${expanded ? " ap2p-panel-chevron-open" : ""}`}
              aria-hidden
            />
          ) : null}
        </span>
      </button>
      <div className={`ap2p-participant-list${isLong && expanded ? " ap2p-participant-list-scroll" : ""}`}>
        {visible.map((participant) => (
          <ParticipantRow
            key={`${participant.userId}-${participant.role}-${participant.inviteStatus || ""}`}
            participant={participant}
            perSeat={perSeat}
          />
        ))}
      </div>
      {isLong ? (
        <button
          type="button"
          className="ap2p-participant-toggle"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
        >
          {expanded ? "Show fewer players" : `Show ${hiddenCount} more player${hiddenCount === 1 ? "" : "s"}`}
          <ChevronDown
            size={14}
            className={`ap2p-panel-chevron${expanded ? " ap2p-panel-chevron-open" : ""}`}
            aria-hidden
          />
        </button>
      ) : null}
    </section>
  );
}
